import {
  ProductCreationRequestStateSchema,
  ProductFormSchema,
} from "../../../../../types/types.PIM";

/**
 * Merge the lightweight `/state` response into the full form the client already
 * holds, without touching the (static) form template structure.
 *
 * The Next button used to re-fetch the entire form GET just to
 * pick up the values saved by the preceding PATCH. The form template never
 * changes during a fill session — only the saved values, filled steps and a few
 * top-level fields do — so we fetch just those from `/state` and splice them
 * back into the existing form items:
 *   - group attribute item   → item.attributes[].values = state.attribute_values[attribute_id]
 *   - collection item        → item.value_rows          = state.value_rows[collection_id]
 *
 * Returns a new object (no mutation of `form`) so React/SWR see a fresh reference.
 */
export function mergeFormState(
  form: ProductFormSchema,
  state: ProductCreationRequestStateSchema
): ProductFormSchema {
  const attributeValues = state.attribute_values ?? {};
  const valueRows = state.value_rows ?? {};

  return {
    ...form,
    product_id: state.product_id ?? form.product_id,
    status: state.status ?? form.status,
    number: state.number ?? form.number,
    product_number: state.product_number ?? form.product_number,
    filled_steps: state.filled_steps ?? form.filled_steps,
    form: form.form && {
      ...form.form,
      steps: form.form.steps?.map((step) => ({
        ...step,
        items: step.items?.map((item) =>
          item.type === "collection_attribute"
            ? {
                ...item,
                value_rows: valueRows[item.group_or_collection_id] ?? [],
              }
            : {
                ...item,
                attributes: item.attributes?.map((attribute) => ({
                  ...attribute,
                  values: attributeValues[attribute.attribute_id] ?? [],
                })),
              }
        ),
      })),
    },
  };
}
